import { useCallback, useEffect, useRef, useState } from 'react';

import { clampHeadingLevel, renderContentHtml } from './contentHtml';
import { buildTableMarkup } from './tableUnitHtml';
import type { HeadingLevel } from './contentHtml';
import type { TableUnitData } from './useComponent';
import styles from './TableUnit.module.css';

interface TableUnitProps {
  headingLevel?: HeadingLevel;
  hideHeading?: boolean;
  unit: TableUnitData;
}

/**
 * Reports whether the scroll container is actually narrower than its content.
 *
 * The scroll region only becomes a tab stop when there is something to scroll:
 * making every table focusable would add a keyboard stop to the majority of
 * tables that fit, which is its own accessibility cost.
 */
function useIsOverflowing() {
  const [isOverflowing, setIsOverflowing] = useState(false);
  const elementRef = useRef<HTMLDivElement | null>(null);

  const measure = useCallback(() => {
    const element = elementRef.current;

    if (!element) {
      return;
    }

    // A one-pixel allowance: sub-pixel layout rounding otherwise reports a
    // fitting table as overflowing.
    setIsOverflowing(element.scrollWidth - element.clientWidth > 1);
  }, []);

  useEffect(() => {
    measure();

    const observer =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;

    if (observer && elementRef.current) {
      observer.observe(elementRef.current);
    }

    window.addEventListener('resize', measure);

    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [measure]);

  return { elementRef, isOverflowing };
}

export function TableUnit({
  headingLevel,
  hideHeading = false,
  unit,
}: TableUnitProps) {
  const HeadingTag = `h${clampHeadingLevel(headingLevel)}` as const;
  const visibleHeading = hideHeading
    ? null
    : unit.numberedHeading ?? unit.heading ?? null;
  const { elementRef, isOverflowing } = useIsOverflowing();

  const markup = buildTableMarkup(unit.html, {
    caption: unit.caption,
    headerRow: unit.headerRow,
  });

  const label = unit.heading ?? unit.caption ?? 'Table';

  return (
    <article className={styles.unit}>
      {visibleHeading ? (
        <HeadingTag className={styles.heading} id={unit.anchorId}>
          {visibleHeading}
        </HeadingTag>
      ) : null}

      <div
        // Scrolls sideways in its own region rather than widening the page.
        // Focusable when it overflows so a keyboard user can reach the columns
        // off screen — a scroll container that cannot take focus is a WCAG
        // 2.1.1 failure. Rows are NOT restacked as cards on narrow screens:
        // the CSS that does that strips the table's implicit ARIA roles.
        aria-label={isOverflowing ? `${label} (scrollable)` : undefined}
        className={[
          styles.scroller,
          styles[unit.style],
          styles[unit.density],
          unit.stickyHeader ? styles.stickyHeader : '',
        ]
          .filter(Boolean)
          .join(' ')}
        data-overflowing={isOverflowing ? 'true' : 'false'}
        ref={elementRef}
        role={isOverflowing ? 'region' : undefined}
        tabIndex={isOverflowing ? 0 : undefined}
      >
        {renderContentHtml(markup)}
      </div>
    </article>
  );
}
